/**
 * Shape of a domain value object stored in `custom_props`.
 *
 * Domain value fields in the backend JSONB column now store an object
 * with both the coded value and a pre-resolved human-readable label:
 * ```json
 * { "value": "N", "displayValue": "Bridge does not carry a toll road" }
 * ```
 *
 * Non-domain-value fields remain raw scalars (string, number, boolean, null).
 */
export interface DomainValueObject {
  /** The coded/stored value (e.g., `"N"`, `"S01"`, `"7"`). */
  readonly value: unknown;
  /** Human-readable label resolved from the domain value mapping. */
  readonly displayValue: string | null;
}

/**
 * Type guard that checks whether a raw custom property value is a domain value object.
 *
 * A domain value object has the shape `{ value: unknown, displayValue: string | null }`.
 * Returns `false` for raw scalars, null, arrays, and objects that don't match.
 *
 * @param raw - The raw value from a data row's custom property field
 * @returns `true` if `raw` is a `DomainValueObject`
 */
export function isDomainValueObject(raw: unknown): raw is DomainValueObject {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    return false;
  }
  return 'value' in raw && 'displayValue' in raw;
}

/**
 * Returns the coded scalar from an element attribute value.
 * Domain value fields are unwrapped to `{ value }`; other values pass through unchanged.
 *
 * @param raw - The raw value to resolve.
 * @returns The resolved value.
 */
export function resolveAttributeRawValue(raw: unknown): unknown {
  if (isDomainValueObject(raw)) {
    return raw.value;
  }
  return raw;
}
